import React, { Fragment, useEffect, useMemo, useState } from 'react';
import QuotationTemplateTable from '#features/QuotationTemplateTable';
import { Drawer, Space, Spin, Switch, Tag } from '@bedrock/components';
import TreeToTable, {
    ShowTree
} from "#components/TreeToTable";
import { $sType } from '#utils';
import { cloneDeep, isNumber } from 'lodash'
import CooperativeContents from '#features/MPCDemand/CooperativeContents';
import { orderNum } from "#utils/number";
import {
    attachmentBlobData,
    DemandCooperateAccountDto,
    demandDiffQuoteDetail,
    demandDiffQuoteDetailMerge,
    outsourceAttachmentData,
    outsourceDemandDiffQuoteDetail,
    outsourceDemandDiffQuoteDetailMerge
} from '#services/api';

import styles from './index.less';


type Props = {
    detailsTableVisible: boolean;
    setDetailsTableVisible: (value: boolean) => void;
    quoteIndex?: number; // 报价下标
    quoteTableConfig: {
        id: number;
        demand_supplier_quote_id: number;
        isBlob: boolean;
        isShowSettle: boolean; // 是否显示结算报价
        isAll: boolean; // 是否为报价总明细
        demand_supplier_id: number;
        cooperate_accounts?: DemandCooperateAccountDto[];
    }
}

type Item = {
    [key: string]: any;
    children?: Item[];
};

// 判断是否是数字，如果是加上千分位
const formatNumber = (n: number | string) => {
    if (isNaN(Number(n))) return n;
    let num = Number(n);
    if (typeof num === 'number' && !isNaN(num)) {
        return num.toString().replace(/\d+/, function (n) { // 先提取整数部分
            return n.replace(/(\d)(?=(\d{3})+$)/g, function ($1) {
                return $1 + ",";
            });
        });
    }
    return num;
};

// 数组合并，根据sameKey的值相同合并为新的children
const mergeByParentKey = function (items: any[], sameKey: string): Item[]  {
    const newItems = JSON.parse(JSON.stringify(items))
    const result = [];
    const obj = {};
    newItems.forEach((item) => {
        if (!obj[item[sameKey]]) {
            obj[item[sameKey]] = {
                ...item,
                children: []
            };
            result.push(obj[item[sameKey]]);
        }
        obj[item[sameKey]].children.push(item)
    })


    result.forEach((item) => {
        if (item.children?.length) {
            if (item.children.length === 1) { // 补充另一条数据
                const currentObi = item.children[0]
                item.children.push({
                    "name": currentObi.name,
                    "l2_category_id": currentObi.l2_category_id,
                    "l2_category_name": currentObi.l2_category_name,
                    "l3_category_name": currentObi.l3_category_name,
                    "col1_value": currentObi.col1_value,
                    "col2_value": currentObi.col2_value,
                    "level": currentObi.level,
                    "children": [],
                    "change": currentObi.change,
                    "quote_stage": currentObi?.quote_stage === "BIDDING_QUOTE" ? "MARKING_QUOTE" : "BIDDING_QUOTE",
                    "quote_stage_name": currentObi?.quote_stage_name === "中标报价" ? "结案报价" : "中标报价"
                })
              
            }
            
            // 对比children两者的值是否相同，如果不同则列出changKey
            const changeKeysSet = new Set<string>();
            if (typeof item.children[0] === 'object') {
                const keysSet = item.children?.reduce((prev, next) => {
                    Object.keys(next).forEach((key) => {
                        prev.add(key);
                    });
                    return prev;
                }, new Set<string>())
                const keys = Array.from(keysSet);
                keys.forEach((key) => {
                    item.children!.some((child) => {
                        if (child[key] !== item.children![0][key]) {
                            changeKeysSet.add(key);
                            return true;
                        }
                        return false;
                    });
                });
            }

            const changeKeys = Array.from(changeKeysSet);
            item.children!.forEach((child, index) => {
                item.children![index] = {
                    ...child,
                    changeKeys
                }
            })

            // MARKING_QUOTE BIDDING_QUOTE
            item.children?.sort((a, b) =>b?.quote_stage?.localeCompare(a?.quote_stage))
        }
    });

    return result;
};

function getShowTree(returnData: ShowTree[] = [], dataTree: Item[] = [], mergeEnd = false): ShowTree[] {
    if (mergeEnd && (!dataTree[0]?.children || !dataTree[0]?.children.length)) {
        const newData = mergeByParentKey(dataTree, 'name');

        newData.forEach((item, index) => {
            returnData.push({
                ...item,
                value: item.name,
                key: String(index),
                id: item.id,
            } as ShowTree);
        });

        return returnData;
    } else {
        dataTree.forEach((item, index) => {
            returnData.push({
                ...item,
                value: item.name,
                key: String(index),
                id: item.id,
                children: getShowTree([], item.children || [], mergeEnd)
            });
        });
    }

    return returnData;
}

// 获取树的层级
function getTreeLevel(data: Item[], level = 0): number {
    if (data?.length) {
        return getTreeLevel(data[0]?.children, level + 1)
    } else {
        return level
    }
}

const onCellMiddle = () => ({ style: { verticalAlign: 'middle' }});

const onCellOddColor = (r, index) => {
    return {
        style: {
            background: index % 2 === 1 ? '#f8f8fa' : 'unset',
            verticalAlign: 'middle',
        }
    }
}

const SINGLE_HEAD = [
    {
        dataIndex: 'level-1',
        width: 200,
        fixed: 'left',
        onCell: onCellMiddle
    },
    {
        dataIndex: 'level-2',
        width: 200,
        fixed: 'left',
        onCell: onCellOddColor
    },
]

const ALL_HEAD = [
    {
        dataIndex: 'level-1',
        width: 320,
        fixed: 'left',
        onCell: onCellMiddle
    },
    {
        dataIndex: 'level-2',
        width: 200,
        fixed: 'left',
        onCell: onCellMiddle
    },
    {
        dataIndex: 'level-3',
        width: 200,
        fixed: 'left',
        onCell: onCellOddColor
    },
]


const DetailsTable = ({ detailsTableVisible, setDetailsTableVisible, quoteTableConfig, quoteIndex }: Props) => {

    const { id, demand_supplier_quote_id, demand_supplier_id, isBlob, isShowSettle, isAll, cooperate_accounts, demand_supplier_quotes } = quoteTableConfig

    const [checkedBiddingQuote, setCheckedBiddingQuote] = useState<boolean>(false) // 查看报价明细 显示中标报价
    const [value, setValue] = useState({} as any)
    const [loading, setLoading] = useState<boolean>(false)
    const $orderIndex = cooperate_accounts?.[quoteIndex]?.$orderIndex;

    // 请求明细
    const fetchQuotationDetails = async () => {

        let result = null

        setLoading(true)
        if ($sType === 'outer') {
            if (isShowSettle) { // 报价阶段 区分价格类型
                if (isAll) {
                    result = await outsourceDemandDiffQuoteDetailMerge({ demand_supplier_id, quote_attachment_id: id, blob: isBlob })
                } else {
                    result = await outsourceDemandDiffQuoteDetail({ demand_supplier_quote_id, quote_attachment_id: id, blob: isBlob })
                }
            } else {
                result = await outsourceAttachmentData({ id: id, download: 'attachment', blob: isBlob })
            }
        } else {
            if (isShowSettle) {
                if (isAll) {
                    result = await demandDiffQuoteDetailMerge({ demand_supplier_id, quote_attachment_id: id, blob: isBlob })
                } else {
                    result = await demandDiffQuoteDetail({ demand_supplier_quote_id, quote_attachment_id: id, blob: isBlob })
                }
            } else {
                result = await attachmentBlobData({ id: id, download: 'attachment', blob: isBlob })
            }
        }
        setLoading(false)
        if (result?.code === 1) {
            const newData = cloneDeep(result?.data);
            // 旧数据表头含有二级需求三级需求、需去除
            if (newData?.head?.some?.(v => ['l2_category_id', 'l3_category_id'].includes(v?.[1]))) {
                newData.list = newData?.list?.[0]?.children?.[0]?.children;
                newData.head = newData?.head?.filter?.(v => !['l2_category_id', 'l3_category_id'].includes(v?.[1]))
            }
            setValue(newData)
        }
    }

    useEffect(() => {
        setCheckedBiddingQuote(false)
        if (detailsTableVisible) {
            fetchQuotationDetails()
        }

    }, [detailsTableVisible])



    const config = useMemo(() => {
        let quoteData = [];
        if (isAll) {
            quoteData = value?.list
            ?.map(v => {
                const cooperate_account_info = cooperate_accounts?.find?.(it => it.id === v?.cooperate_account_id)
                return {
                    ...v,
                    name: <CooperativeContents 
                        Cooperative={cooperate_account_info} 
                        index_id={v.index_id} 
                        showDetailBtn={false} 
                        titleClassName={styles['cooperative-contents-title']}
                        tagClassName={styles['cooperative-contents-tag']}
                        />,
                }
            })
            if (isShowSettle) {
                // 三级单过滤掉未中选的合作内容
                quoteData = quoteData?.filter(v => demand_supplier_quotes?.find(it => it.cooperate_account_id === v?.cooperate_account_id))
            }
        } else {
            quoteData = value?.list
        }
        return {
            titleList: value?.head?.map(n => {
                return {
                    text: n?.[0],
                }
            }) || [],
            treeData: getShowTree([], quoteData || [], true),
            treeLevel: getTreeLevel(value?.list || []), // 根据数据模型判断
        }
    }, [value])

    const moreColumns = useMemo(() => {
        // 根据level截取前面的head
        let moreHead = value?.head?.slice(config.treeLevel) || []
        moreHead = moreHead.filter(n => {
            if (!isShowSettle) return n?.[1] !== 'quote_stage_name' // 非报价阶段 去掉价格类型列
            return true
        })
        moreHead = moreHead.map((n, index) => {
            const type = n?.[1];
            // 价格类型 & 总价
            const isFixed = ['total_price', 'quote_stage_name'].includes(type) ? 'right' : undefined

            // 价格类型
            if (n?.[1] === 'quote_stage_name') {
                return {
                    title: n?.[0],
                    dataIndex: ['$last', n?.[1] || index],
                    key: `col${index}`,
                    align: 'center',
                    fixed: isFixed,
                    width: 100,
                    onCell: onCellOddColor,
                    render: (text: any, record: any) => {
                        return <Tag text={text} color={record.$last.quote_stage === "BIDDING_QUOTE" ? 'light-green' : 'light-blue'} size='xSmall' />
                    }
                }
            }

            return {
                title: n?.[0],
                dataIndex: ['$last', n?.[1] || index],
                key: `col${index}`,
                align: 'right',
                fixed: isFixed,
                width: 160,
                onCell: onCellOddColor,
                render: (text: any, record: any) => {
                    const changeKeys = record.$last.changeKeys || [] // 明细有变化 高亮
                    const isChange = changeKeys.includes(n?.[1]) && checkedBiddingQuote && isShowSettle
                    return <span
                        style={{
                            display: 'inline-block',
                            maxWidth: '200px',
                            color: isChange ? '#E08216' : 'unset',
                            fontWeight: ['total_price'].includes(type) ? 'bold' : 'unset'
                    }}
                    >{formatNumber(text) || '--'}</span>
                }
            }
        })


        return [...(isAll ? ALL_HEAD : SINGLE_HEAD), ...moreHead]
    }, [value, config, checkedBiddingQuote])

    return (
        <Drawer
            className={styles.detail_table}
            title={isAll ? '报价总明细' : `合作内容${orderNum($orderIndex, quoteIndex)}-报价明细`}
            placement="bottom"
            visible={detailsTableVisible}
            bodyStyle={{ paddingTop: 0 }}
            onClose={() => {
                setDetailsTableVisible(false)
            }}
        >

            {loading ? <Spin.Global spinning={loading} tip="数据加载中..." /> : (
                <Fragment>
                    {/* 报价阶段 展示中标价格 */}
                    {
                        isShowSettle ? (
                            <div className={styles.details_table_top_options}>
                                <Space size={4}>
                                    <span>是否显示中标报价</span>
                                    <Switch checked={checkedBiddingQuote} onChange={setCheckedBiddingQuote} />
                                </Space>
                                <Space size={6}>
                                    <span className={styles.details_table_top_tips}></span>
                                    <span className={styles.details_table_top_tips_text}>变更对比</span>
                                </Space>
                            </div>
                        ) : null
                    }

                    <TreeToTable
                        value={config.treeData}
                        onChange={null}
                        tableTitleList={config.titleList}
                        renderCellValue={({ value }) => {
                            return value
                        }}
                        treeLevel={config.treeLevel}
                        tableProps={{
                            hoverColor: false,
                            sticky: true,
                            columns: moreColumns,
                            scroll: { x: 'max-content', y: 600 },
                            style: { marginBottom: '16px' },
                        }}
                        tableDataFilter={(item) => {
                            // 筛选
                            if (item?.$last.quote_stage === "BIDDING_QUOTE" && !checkedBiddingQuote) return false;
                            return true
                        }}
                    />
                </Fragment>
            )}


        </Drawer>
    )
}


export default DetailsTable;